import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import fetchWithAuth from "../fetchWithAuth";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "./ui/accordion";
import { Separator } from "./ui/separator";
import { FileX2 } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import ProjectNotices from "./ProjectNotices";
import ProjectProcessing from "./ProjectProcessing";
import { TasksToday } from "./TasksOverview";
import GroupReportButton from "./GroupReportButton";

const API_URL = "/api/projects";
const statusOf = project => project.isDraft ? "draft" : project.isActive !== false ? "active" : "inactive";

export default function ProjectList() {
  const [projects, setProjects] = useState([]);
  const [projectStatus, setProjectStatus] = useState("active");
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkAction, setBulkAction] = useState(null);
  const [notice, setNotice] = useState("");

  // Сброс title при переходе на общую страницу проектов
  React.useEffect(() => {
    document.title = "Behavioral Factor Studio";
  }, []);
  const [name, setName] = useState("");
  const [dailyClicks, setDailyClicks] = useState("");
  const [group, setGroup] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // eslint-disable-next-line no-unused-vars
  const [tasksSum, setTasksSum] = useState(null);
  const [projectsData, setProjectsData] = useState({});
  const [scheduledTasksData, setScheduledTasksData] = useState({});
  const [openGroups, setOpenGroups] = useState([]);
  const [copiedGroupId, setCopiedGroupId] = useState(null);
  const visibleProjects = projects.filter(project => statusOf(project) === projectStatus);
  const activeCount = projects.filter(project => statusOf(project) === "active").length;
  const inactiveCount = projects.filter(project => statusOf(project) === "inactive").length;
  const draftCount = projects.filter(project => statusOf(project) === "draft").length;
  const visibleIds = visibleProjects.map(project => project.id);

  const toggleSelection = (ids, checked) => {
    setSelectedIds(current => checked ? [...new Set([...current, ...ids])] : current.filter(id => !ids.includes(id)));
  };

  const handleBulkAction = async (action) => {
    if (!selectedIds.length) return;
    setBulkAction(action);
    setError("");
    setNotice("");
    try {
      const res = await fetchWithAuth(`${API_URL}/bulk-action`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action, projectIds: selectedIds }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Не удалось выполнить групповое действие");
      if (action === "rebuild") {
        const queued = data.queuedProjectIds?.length || 0;
        setNotice(`Расписания пересобраны: ${data.count - queued}. Ожидают свежую выдачу: ${queued}.`);
        const errors = data.results?.filter(result => result.error) || [];
        if (errors.length) setError(errors.map(result => `Проект #${result.projectId}: ${result.error}`).join("; "));
        await fetchScheduledTasksData();
      } else {
        setNotice(`${action === "start" ? "Запущено" : "Остановлено"} проектов: ${data.count}`);
        await fetchProjects();
      }
      setSelectedIds([]);
    } catch (e) {
      setError(e.message);
    } finally {
      setBulkAction(null);
    }
  };

  useEffect(() => {
    setSelectedIds(current => current.filter(id => projects.some(project => project.id === id &&
      statusOf(project) === projectStatus)));
  }, [projects, projectStatus]);

  // Загрузка проектов
  const fetchProjects = async () => {
    setLoading(true);
    try {
      const res = await fetchWithAuth(API_URL);
      const data = await res.json();
      if (Array.isArray(data)) {
        setProjects(data);
      } else if (data && data.error) {
        setError(data.error);
        setProjects([]);
      } else {
        setError("Ошибка загрузки проектов");
        setProjects([]);
      }
    } catch (e) {
      setError("Ошибка загрузки проектов");
      setProjects([]);
    }
    setLoading(false);
  };

  // Загрузка данных о запланированных задачах
  const fetchScheduledTasksData = async () => {
    try {
      const res = await fetchWithAuth("/api/scheduled-tasks-summary");
      const data = await res.json();
      setScheduledTasksData(data);
    } catch (e) {
      console.error("Ошибка загрузки данных о запланированных задачах:", e);
      setScheduledTasksData({});
    }
  };

  // Загрузка дополнительных данных для проектов
  const fetchProjectsData = React.useCallback(async () => {
    if (!projects.length) return;
    
    const data = {};
    for (const project of projects) {
      data[project.id] = {
        queries: 0,
        promotedPages: 0,
        blacklistPages: 0
      };
      
      try {
        // Загрузка данных проекта с деталями
        const projectRes = await fetchWithAuth(`/api/project/${project.id}`);
        const projectData = await projectRes.json();
        
        // Подсчет количества записей
        if (projectData.queries) {
          data[project.id].queries = projectData.queries.length || 0;
        }
        
        if (projectData.promotedPages) {
          data[project.id].promotedPages = projectData.promotedPages.filter(p => p.isActive).length || 0;
        }
        
        if (projectData.blacklistPages) {
          data[project.id].blacklistPages = projectData.blacklistPages.length || 0;
        }
      } catch (e) {
        console.error(`Ошибка загрузки данных для проекта ${project.id}:`, e);
      }
    }
    
    setProjectsData(data);
  }, [projects]);

  useEffect(() => {
    fetchProjects();
    fetchScheduledTasksData();
  }, []);
  
  // Установка открытых групп при загрузке проектов
  useEffect(() => {
    if (projects.length) {
      const groupIds = [...new Set(projects.map(p => p.group || 1))].map(id => id.toString());
      setOpenGroups(groupIds);
    }
  }, [projects]);

  useEffect(() => {
    if (projects.length) {
      fetchProjectsData();
    }
  }, [projects, fetchProjectsData]);

  // Добавление проекта
  const handleAdd = async (e) => {
    e.preventDefault();
    setError("");
    if (!name.trim()) {
      setError("Укажите название проекта");
      return;
    }
    const clicks = dailyClicks === "" ? 10 : Number(dailyClicks);
    const projectGroup = group === "" ? 1 : Number(group);
    if (!Number.isInteger(clicks) || clicks < 10 || clicks > 1000) {
      setError("Количество кликов должно быть целым числом от 10 до 1000");
      return;
    }
    if (!Number.isInteger(projectGroup) || projectGroup < 1) {
      setError("Группа должна быть положительным числом");
      return;
    }
    try {
      const res = await fetchWithAuth(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), dailyClicks: clicks, group: projectGroup }),
      });
      if (!res.ok) {
        const data = await res.json();
        setError(data.error || "Ошибка при добавлении проекта");
        return;
      }
      setName("");
      setDailyClicks("");
      setGroup("");
      setProjectStatus("draft");
      fetchProjects();
    } catch {
      setError("Ошибка при добавлении проекта");
    }
  };

  // Группировка проектов
  const groupedProjects = visibleProjects.reduce((acc, project) => {
    const groupId = project.group || 1;
    if (!acc[groupId]) {
      acc[groupId] = [];
    }
    acc[groupId].push(project);
    return acc;
  }, {});

  // Расчет суммарных значений для каждой группы
  const groupSummaries = Object.keys(groupedProjects).reduce((acc, groupId) => {
    const projects = groupedProjects[groupId];
    acc[groupId] = {
      dailyClicks: projects.reduce((sum, p) => sum + p.dailyClicks, 0),
      queries: projects.reduce((sum, p) => sum + (projectsData[p.id]?.queries || 0), 0),
      promotedPages: projects.reduce((sum, p) => sum + (projectsData[p.id]?.promotedPages || 0), 0),
      blacklistPages: projects.reduce((sum, p) => sum + (projectsData[p.id]?.blacklistPages || 0), 0),
      completedTasks: projects.reduce((sum, p) => sum + (scheduledTasksData[p.id]?.completed || 0), 0),
      totalTasks: projects.reduce((sum, p) => sum + (scheduledTasksData[p.id]?.total || 0), 0)
    };
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <TasksToday refreshKey={scheduledTasksData} />
      <ProjectNotices />
      <ProjectProcessing />
      {/* Форма добавления проекта */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-xl font-semibold mb-2">Создать проект</h2>
        <p className="text-sm text-gray-500 mb-4">Проект создается как черновик. Добавьте ключи и страницы, затем запустите его в настройках проекта. По умолчанию: 10 кликов, группа 1.</p>
        <form onSubmit={handleAdd} className="flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium mb-1">Название</label>
            <Input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Название проекта"
            />
          </div>
          <div className="w-[180px]">
            <label className="block text-sm font-medium mb-1">Кликов в день</label>
            <Input
              type="number"
              value={dailyClicks}
              onChange={e => setDailyClicks(e.target.value)}
              min={10}
              max={1000}
              placeholder="10-1000"
            />
          </div>
          <div className="w-[120px]">
            <label className="block text-sm font-medium mb-1">Группа</label>
            <Input
              type="number"
              value={group}
              onChange={e => setGroup(e.target.value)}
              min={1}
              placeholder="Группа"
            />
          </div>
          <Button type="submit" className="h-10">
            Создать черновик
          </Button>
        </form>
      </div>

      {/* Список проектов по группам */}
      <Tabs value={projectStatus} onValueChange={value => { setProjectStatus(value); setSelectedIds([]); }}>
        <TabsList aria-label="Состояние проектов">
          <TabsTrigger value="active" disabled={Boolean(bulkAction)}>Активные ({activeCount})</TabsTrigger>
          <TabsTrigger value="inactive" disabled={Boolean(bulkAction)}>Неактивные ({inactiveCount})</TabsTrigger>
          <TabsTrigger value="draft" disabled={Boolean(bulkAction)}>Черновики ({draftCount})</TabsTrigger>
        </TabsList>
        <TabsContent value={projectStatus} className="space-y-4 mt-4">
          <div className="bg-white p-4 rounded-lg shadow flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                aria-label="Выбрать все проекты на вкладке"
                checked={visibleIds.length > 0 && visibleIds.every(id => selectedIds.includes(id))}
                ref={element => { if (element) element.indeterminate = selectedIds.length > 0 && selectedIds.length < visibleIds.length; }}
                onChange={event => toggleSelection(visibleIds, event.target.checked)}
                disabled={!visibleIds.length || Boolean(bulkAction)}
                className="h-4 w-4 accent-primary"
              />
              Выбрано: {selectedIds.length}
            </label>
            <Button variant="outline" size="sm" onClick={() => handleBulkAction("stop")}
              disabled={!selectedIds.length || projectStatus !== "active" || Boolean(bulkAction)}>
              {bulkAction === "stop" ? "Остановка…" : "Остановить"}
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleBulkAction("start")}
              disabled={!selectedIds.length || projectStatus === "active" || Boolean(bulkAction)}>
              {bulkAction === "start" ? "Запуск…" : "Запустить"}
            </Button>
            <Button size="sm" aria-label="Пересобрать расписание выбранных проектов" onClick={() => handleBulkAction("rebuild")}
              disabled={!selectedIds.length || projectStatus !== "active" || Boolean(bulkAction)}>
              {bulkAction === "rebuild" ? "Запуск пересбора…" : "Пересобрать расписание"}
            </Button>
          </div>
          {projectStatus === "active" && <p className="text-sm text-gray-500">При остановке проекта все его задания удаляются из расписания. Аналитика и ссылки сохраняются.</p>}
          {error && <p role="alert" className="text-red-600">{error}</p>}
          {notice && <p role="status" className="text-green-700">{notice}</p>}
      <div className="space-y-4">
        <Accordion 
          type="multiple" 
          value={openGroups}
          onValueChange={setOpenGroups}
        >
          {Object.keys(groupedProjects)
            .sort((a, b) => Number(a) - Number(b))
            .map(groupId => (
              <AccordionItem key={groupId} value={groupId} className="bg-white rounded-lg shadow overflow-hidden border-0 mb-4">
                <div className="flex items-center bg-secondary pr-4">
                <AccordionTrigger className="bg-secondary px-4 py-3 text-primary font-semibold hover:no-underline">
                  <div className="flex items-center gap-2 w-full">
                    <span>Группа {groupId}</span>
                    <div className="relative">
                      <Badge 
                        variant="outline"
                        className="flex items-center gap-1 cursor-pointer bg-white"
                        title="Ссылка на блеклист"
                        onClick={(e) => {
                          e.stopPropagation();
                          const blacklistUrl = `${window.location.origin}/blacklist-group-${groupId}.txt`;
                          
                          // Создаем временный элемент textarea
                          const textarea = document.createElement('textarea');
                          textarea.value = blacklistUrl;
                          textarea.style.position = 'fixed';  // Вне видимой области
                          document.body.appendChild(textarea);
                          textarea.select();
                          
                          try {
                            // Пытаемся скопировать текст в буфер обмена
                            document.execCommand('copy');
                            setCopiedGroupId(groupId);
                            setTimeout(() => setCopiedGroupId(null), 2000);
                          } catch (err) {
                            console.error('Не удалось скопировать текст: ', err);
                          } finally {
                            // Удаляем временный элемент
                            document.body.removeChild(textarea);
                          }
                        }}
                      >
                        <FileX2 className="h-4 w-4" />
                        <span>Blacklist</span>
                      </Badge>
                      {copiedGroupId === groupId && (
                        <div className="absolute left-full ml-2 top-0 bg-black text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                          Ссылка скопирована
                        </div>
                      )}
                    </div>
                  </div>
                </AccordionTrigger>
                <GroupReportButton groupId={groupId} />
                </div>
                <AccordionContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="px-4 py-2 w-12">
                            <input
                              type="checkbox"
                              aria-label={`Выбрать проекты группы ${groupId}`}
                              checked={groupedProjects[groupId].every(project => selectedIds.includes(project.id))}
                              ref={element => {
                                if (element) {
                                  const count = groupedProjects[groupId].filter(project => selectedIds.includes(project.id)).length;
                                  element.indeterminate = count > 0 && count < groupedProjects[groupId].length;
                                }
                              }}
                              onChange={event => toggleSelection(groupedProjects[groupId].map(project => project.id), event.target.checked)}
                              disabled={Boolean(bulkAction)}
                              className="h-4 w-4 accent-primary"
                            />
                          </th>
                          <th className="px-4 py-2 text-left w-[250px]">Название проекта</th>
                          <th className="px-4 py-2 text-left">Ключей в работе</th>
                          <th className="px-4 py-2 text-left">Страниц в продвижении</th>
                          <th className="px-4 py-2 text-left">Страниц на вытеснении</th>
                          <th className="px-4 py-2 text-center">Статистика заданий<br/>отправлено/план/максимум</th>
                          <th className="px-4 py-2 text-left"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {groupedProjects[groupId].map(project => (
                          <tr key={project.id} className="border-t">
                            <td className="px-4 py-2">
                              <input
                                type="checkbox"
                                aria-label={`Выбрать проект ${project.name}`}
                                checked={selectedIds.includes(project.id)}
                                onChange={event => toggleSelection([project.id], event.target.checked)}
                                disabled={Boolean(bulkAction)}
                                className="h-4 w-4 accent-primary"
                              />
                            </td>
                            <td className="px-4 py-2">
                              <Link to={`/project/${project.id}`} className="text-primary hover:underline">
                                {project.name}
                              </Link>
                            </td>
                            <td className="px-4 py-2">{projectsData[project.id]?.queries || 0}</td>
                            <td className="px-4 py-2">{projectsData[project.id]?.promotedPages || 0}</td>
                            <td className="px-4 py-2">{projectsData[project.id]?.blacklistPages || 0}</td>
                            <td className="px-4 py-2 text-center">
                              <div className="inline-flex items-center justify-center bg-[#f1f5f9] rounded-full px-3 py-1 w-[150px]">
                                <span>{scheduledTasksData[project.id]?.completed || 0}</span>
                                <Separator orientation="vertical" className="mx-2 h-4 bg-white" />
                                <span>{scheduledTasksData[project.id]?.total || 0}</span>
                                <Separator orientation="vertical" className="mx-2 h-4 bg-white" />
                                <span>{project.dailyClicks}</span>
                              </div>
                            </td>
                            <td className="px-4 py-2">
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-red-600 border-red-200 hover:text-red-600 hover:bg-red-50"
                                onClick={async () => {
                                  if (window.confirm("Удалить проект?")) {
                                    try {
                                      await fetchWithAuth(`${API_URL}/${project.id}`, { method: "DELETE" });
                                      fetchProjects();
                                    } catch {
                                      setError("Ошибка при удалении проекта");
                                    }
                                  }
                                }}
                              >
                                Удалить
                              </Button>
                            </td>
                          </tr>
                        ))}
                        {/* Суммарные значения для группы */}
                        <tr className="bg-gray-50 font-semibold h-12">
                          <td></td>
                          <td className="px-4 py-2 text-right">Итого:</td>
                          <td className="px-4 py-2">{groupSummaries[groupId].queries}</td>
                          <td className="px-4 py-2">{groupSummaries[groupId].promotedPages}</td>
                          <td className="px-4 py-2">{groupSummaries[groupId].blacklistPages}</td>
                          <td className="px-4 py-2 text-center">
                            <div className="inline-flex items-center justify-center bg-[#f1f5f9] rounded-full px-3 py-1 w-[150px]">
                              <span>{groupSummaries[groupId].completedTasks}</span>
                              <Separator orientation="vertical" className="mx-2 h-4 bg-white" />
                              <span>{groupSummaries[groupId].totalTasks}</span>
                              <Separator orientation="vertical" className="mx-2 h-4 bg-white" />
                              <span>{groupSummaries[groupId].dailyClicks}</span>
                            </div>
                          </td>
                          <td className="px-4 py-2"></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
        </Accordion>
        
        {visibleProjects.length === 0 && !loading && (
          <div className="bg-white p-8 rounded-lg shadow text-center text-gray-500">
            {projectStatus === "active" ? "Нет активных проектов" : projectStatus === "draft" ? "Нет черновиков" : "Нет неактивных проектов"}
          </div>
        )}
      </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
